// Shared work-queue item row, used by both the Work Queue page and the Dashboard card.
import { X } from 'lucide-react';

export const URGENCY = {
  critical: { border: '#E5441A', badgeBg: '#FEF2F2', badgeText: '#E5441A', date: '#E5441A' },
  warning: { border: '#D97706', badgeBg: '#FFFBEB', badgeText: '#D97706', date: '#D97706' },
  upcoming: { border: '#1B4A76', badgeBg: '#EFF6FF', badgeText: '#1B4A76', date: '#1B4A76' },
};

export const MODULE_LABELS = {
  violations: 'Violation',
  dot_permits: 'DOT Permit',
  dob_filings: 'DOB Filing',
  fdny_inspections: 'FDNY Inspection',
  compliance: 'Compliance',
  cotco: 'CO/TCO',
  tasks: 'Task',
  proposals: 'Proposal',
};

export function badgeText(days) {
  if (days < 0) return 'Overdue';
  if (days === 0) return 'Today';
  return `${days} days`;
}

export function daysText(days) {
  if (days < 0) return `${Math.abs(days)} days overdue`;
  if (days === 0) return 'Due today';
  return `${days} days remaining`;
}

export function fmtDate(iso) {
  if (!iso) return '\u00A0';
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function WorkQueueRow({ item, onGo, onDismiss }) {
  const u = URGENCY[item.urgency] || URGENCY.upcoming;
  return (
    <div
      className="flex items-stretch transition-colors hover:bg-[#FAFBFC] cursor-pointer"
      onClick={() => onGo(item.action_url)}
      style={{ borderBottom: '1px solid #E8EDF2', borderLeft: `3px solid ${u.border}`, minHeight: '48px' }}
      data-testid={`wq-row-${item.id}`}
      data-urgency={item.urgency}
      data-type={item.type}
    >
      {/* LEFT ZONE */}
      <div className="flex items-center justify-center shrink-0 px-2" style={{ width: '68px' }}>
        <span
          style={{ backgroundColor: u.badgeBg, color: u.badgeText, borderRadius: '2px', fontSize: '12px', fontWeight: 500, padding: '2px 6px', whiteSpace: 'nowrap', fontFamily: "'DM Sans', sans-serif" }}
          data-testid={`wq-badge-${item.id}`}
        >
          {badgeText(item.days_until)}
        </span>
      </div>

      {/* CENTER ZONE */}
      <div className="flex-1 min-w-0 flex flex-col justify-center py-2 pr-3">
        <p className="truncate" style={{ fontSize: '14px', fontWeight: 500, color: '#010A21' }}>{item.title}</p>
        {item.subtitle ? <p className="truncate" style={{ fontSize: '14px', color: '#031333' }}>{item.subtitle}</p> : null}
        <span
          className="mt-1 self-start"
          style={{ backgroundColor: '#F1F5F8', color: '#031333', borderRadius: '2px', fontSize: '12px', padding: '2px 6px', fontFamily: "'DM Sans', sans-serif" }}
          data-testid={`wq-module-${item.id}`}
        >
          {MODULE_LABELS[item.source_module] || item.source_module}
        </span>
      </div>

      {/* RIGHT ZONE */}
      <div className="flex flex-col items-end justify-center shrink-0 py-2 pr-4" style={{ width: '180px' }}>
        <p style={{ fontFamily: "'DM Mono', monospace", fontSize: '14px', color: u.date }}>{fmtDate(item.date)}</p>
        <p style={{ fontSize: '12px', color: '#031333' }}>{daysText(item.days_until)}</p>
        <button onClick={(e) => { e.stopPropagation(); onGo(item.action_url); }} className="hover:underline" style={{ fontSize: '14px', color: '#1B4A76' }} data-testid={`wq-go-${item.id}`}>
          Go →
        </button>
      </div>

      {/* DISMISS (acknowledge) - only when a handler is provided */}
      {onDismiss && (
        <div className="flex items-center justify-center shrink-0 pr-3">
          <button
            onClick={(e) => { e.stopPropagation(); onDismiss(item); }}
            title="Dismiss (acknowledge)"
            aria-label="Dismiss item"
            className="p-1.5 rounded-md text-[#8A94A6] hover:text-[#E5441A] hover:bg-[#FEF2F2] transition-colors"
            data-testid={`wq-dismiss-${item.id}`}
          >
            <X style={{ height: '16px', width: '16px' }} />
          </button>
        </div>
      )}
    </div>
  );
}
